/**
 * Pricing options — miroir frontend de backend/services/optionsPricing.js
 */

export function isOptionMulti(option) {
  if (!option) return false;
  if (option.type === 'CHOIX_MULTIPLE') return true;
  if (option.type === 'CHOIX_UNIQUE') return false;
  return Boolean(option.choixMultiple);
}

export function getMaxSelectable(option) {
  if (!option) return Infinity;
  const max = option.choixMaximum;
  if (Number.isFinite(max) && max > 0) return max;
  return Infinity;
}

export function getTotalSelected(counts) {
  return Object.values(counts || {}).reduce((sum, val) => sum + (Number(val) || 0), 0);
}

function flattenMultiUnits(optionDef, counts) {
  const units = [];
  for (const [choixId, count] of Object.entries(counts || {})) {
    const n = Math.max(0, parseInt(count, 10) || 0);
    if (n <= 0) continue;
    const choix = (optionDef.choix || []).find((c) => c.id === choixId);
    if (!choix || choix.actif === false) continue;
    const prix = Number(choix.prix) || 0;
    for (let i = 0; i < n; i++) {
      units.push({ choixId, prix, nom: choix.nom });
    }
  }
  return units;
}

export function computeMultiGroupExtraPrice(optionDef, counts, productQty = 1) {
  const units = flattenMultiUnits(optionDef, counts);
  const qty = Math.max(1, parseInt(productQty, 10) || 1);
  const nbInclusPerUnit = Math.max(0, parseInt(optionDef?.nbInclus, 10) || 0);
  const totalIncluded = nbInclusPerUnit * qty;

  let total = 0;
  units.forEach((unit, index) => {
    if (index >= totalIncluded) {
      total += unit.prix;
    }
  });
  return Math.round(total * 100) / 100;
}

export function computeOptionsExtraFromSelections(produit, selectedSingleOptions, selectedMultiOptionCounts, productQty = 1) {
  const options = produit?.options || [];
  const qty = Math.max(1, parseInt(productQty, 10) || 1);
  let total = 0;

  options.forEach((option) => {
    if (!Array.isArray(option.choix) || option.choix.length === 0) return;

    if (isOptionMulti(option)) {
      const counts = selectedMultiOptionCounts[option.id] || {};
      total += computeMultiGroupExtraPrice(option, counts, qty);
    } else {
      const selectedId = selectedSingleOptions[option.id];
      if (!selectedId) return;
      const choix = option.choix.find((c) => c.id === selectedId);
      if (!choix) return;
      total += (Number(choix.prix) || 0) * qty;
    }
  });

  return Math.round(total * 100) / 100;
}

export function validateOptionsSelectionDetailed(produit, selectedSingleOptions, selectedMultiOptionCounts) {
  const errors = [];
  (produit?.options || []).forEach((option) => {
    if (!Array.isArray(option.choix) || option.choix.length === 0) return;

    if (isOptionMulti(option)) {
      const counts = selectedMultiOptionCounts[option.id] || {};
      const total = getTotalSelected(counts);
      const min = Math.max(0, parseInt(option.choixMinimum, 10) || 0);
      const max = getMaxSelectable(option);
      const requiredMin = option.obligatoire ? Math.max(min, 1) : min;

      if (requiredMin > 0 && total < requiredMin) {
        errors.push({
          option,
          message: `Sélectionnez au moins ${requiredMin} choix pour « ${option.nom} ».`,
        });
      } else if (Number.isFinite(max) && max < Infinity && total > max) {
        errors.push({
          option,
          message: `Maximum ${max} choix pour « ${option.nom} ».`,
        });
      }
    } else if (option.obligatoire && !selectedSingleOptions[option.id]) {
      errors.push({
        option,
        message: `Choisissez une option pour « ${option.nom} ».`,
      });
    }
  });
  return errors;
}

export function getOptionSelectionHint(option) {
  if (!option || !isOptionMulti(option)) return null;
  const parts = [];
  const nbInclus = Math.max(0, parseInt(option.nbInclus, 10) || 0);
  if (nbInclus > 0) {
    parts.push(`${nbInclus} inclus${nbInclus > 1 ? 'es' : 'e'} gratuitement`);
  }
  const min = Math.max(0, parseInt(option.choixMinimum, 10) || 0);
  const effectiveMin = option.obligatoire ? Math.max(min, 1) : min;
  const max = getMaxSelectable(option);
  if (effectiveMin > 0 && max < Infinity) {
    parts.push(`choisissez ${effectiveMin} à ${max}`);
  } else if (effectiveMin > 0) {
    parts.push(`minimum ${effectiveMin}`);
  } else if (max < Infinity) {
    parts.push(`maximum ${max}`);
  } else if (nbInclus === 0) {
    parts.push('suppléments optionnels');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function getMaxSelectableForOption(option) {
  return getMaxSelectable(option);
}
